import { useMemo, useState, type FormEvent } from 'react';
import { useCreatePayment } from '@repo/queries';
import type { Invoice, PaymentMethod } from '@repo/api-client/dtos';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { describeAppError } from '@/features/common/describeAppError';
import { parseUserMoney } from '@/features/money';
import { minorToDecimal } from '@/features/invoice/invoiceFormSchema';

interface RecordPaymentDialogProps {
  invoice: Invoice;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const METHODS: { value: PaymentMethod; label: string }[] = [
  { value: 'bank_transfer', label: 'Bank transfer' },
  { value: 'credit_card', label: 'Credit card' },
  { value: 'debit_card', label: 'Debit card' },
  { value: 'cash', label: 'Cash' },
  { value: 'check', label: 'Check' },
  { value: 'paypal', label: 'PayPal' },
  { value: 'wire_transfer', label: 'Wire transfer' },
  { value: 'online_banking', label: 'Online banking' },
  { value: 'mobile_payment', label: 'Mobile payment' },
  { value: 'cryptocurrency', label: 'Crypto' },
  { value: 'other', label: 'Other' },
];

export function RecordPaymentDialog({
  invoice,
  open,
  onOpenChange,
}: RecordPaymentDialogProps) {
  const createPayment = useCreatePayment();
  const { toast } = useToast();

  const remainingDecimal = useMemo(
    () => minorToDecimal(invoice.totals.amountDue, invoice.currency),
    [invoice.totals.amountDue, invoice.currency],
  );

  const [amount, setAmount] = useState(remainingDecimal);
  const [method, setMethod] = useState<PaymentMethod>('bank_transfer');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [paymentDate, setPaymentDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    const parsed = parseUserMoney(amount, invoice.currency);
    if (!parsed.ok) {
      setError('Enter a valid amount.');
      return;
    }
    try {
      await createPayment.mutateAsync({
        invoiceId: invoice.id,
        body: {
          amount: parsed.value.amount.toString(),
          currency: invoice.currency,
          method,
          type: 'payment',
          status: 'completed',
          paymentDate: new Date(paymentDate).toISOString(),
          ...(reference ? { reference } : {}),
          ...(notes ? { notes } : {}),
        },
      });
      toast({
        title: 'Payment recorded',
        description: `${invoice.invoiceNumber} balance updated.`,
        variant: 'success',
      });
      onOpenChange(false);
    } catch (err) {
      const desc = describeAppError(err);
      toast({ title: desc.title, description: desc.message, variant: 'danger' });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Record payment</DialogTitle>
          <DialogDescription>
            Logs a payment against {invoice.invoiceNumber}. The invoice flips to
            paid once the balance reaches zero.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label={`Amount (${invoice.currency})`} htmlFor="amount" required error={error ?? undefined}>
              <Input
                id="amount"
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </Field>
            <Field label="Method" htmlFor="method">
              <Select value={method} onValueChange={(v) => setMethod(v as PaymentMethod)}>
                <SelectTrigger id="method">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {METHODS.map((m) => (
                    <SelectItem key={m.value} value={m.value}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Date received" htmlFor="paymentDate">
              <Input
                id="paymentDate"
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
              />
            </Field>
            <Field label="Reference" htmlFor="reference">
              <Input
                id="reference"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
              />
            </Field>
          </div>
          <Field label="Notes" htmlFor="notes">
            <Textarea
              id="notes"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Field>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" loading={createPayment.isPending}>
              Record payment
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
